'use client';

import { useState, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────────────────────
//  WordPress Config Hook
//  Fetches game config (including outfits) from the WP REST API
//  Endpoint: /wp-json/surfsterre/v1/config
//  Falls back to empty outfits array if WP is unreachable.
// ─────────────────────────────────────────────────────────────

export interface WPOutfit {
  id: number;
  name: string;
  description: string;
  previewGif: string;
  spriteSheet: string;
  price: number;
  unlocked: boolean;
  tags: string[];
  accentColor: string;
}

export interface WPGameConfig {
  outfits: WPOutfit[];
  video_loading_screen?: string;
  video_game_intro?: string;
  video_wipeouts?: string[];
  video_combo_hype?: string[];
  combo_window_ms?: number;
  combo_streak_threshold?: number;
  combo_streak_multiplier?: number;
  gameplay_starting_health?: number;
  gameplay_hit_damage?: number;
  gameplay_base_speed?: number;
  gameplay_player_move_speed?: number;
}

// The WordPress site URL — update this to your WP domain
const WP_CONFIG_URL = 'https://squish.games/wp-json/surfsterre/v1/config';

// Cache key for localStorage
const CACHE_KEY = 'surfsterre_wp_config_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedConfig {
  data: WPGameConfig;
  fetchedAt: number;
}

function loadCache(): WPGameConfig | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached: CachedConfig = JSON.parse(raw);
    if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) return null;
    return cached.data;
  } catch {
    return null;
  }
}

function saveCache(data: WPGameConfig) {
  try {
    const cached: CachedConfig = { data, fetchedAt: Date.now() };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cached));
  } catch {
    // ignore storage errors
  }
}

export interface UseWordPressConfigResult {
  config: WPGameConfig | null;
  outfits: WPOutfit[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useWordPressConfig(): UseWordPressConfigResult {
  const [config, setConfig] = useState<WPGameConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Try cache first for instant load
    const cached = loadCache();
    if (cached) {
      setConfig(cached);
      setLoading(false);
    }

    try {
      const res = await fetch(WP_CONFIG_URL, {
        headers: { 'Accept': 'application/json' },
        // 8 second timeout
        signal: AbortSignal.timeout(8000),
      });

      if (!res.ok) throw new Error(`WP config fetch failed: ${res.status}`);

      const json = await res.json();
      // WP endpoint returns { success, version, config }
      const wpConfig: WPGameConfig = json.config ?? json;

      saveCache(wpConfig);
      setConfig(wpConfig);
      console.log('[WP Config] ✅ Loaded', wpConfig.outfits?.length ?? 0, 'outfits from WordPress');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      console.warn('[WP Config] ⚠️ Could not fetch from WordPress:', msg);
      setError(msg);
      // If no cache was loaded, set empty config so game still works
      if (!cached) {
        setConfig({ outfits: [] });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Listen for postMessage CONFIG_UPDATE from WP admin bridge
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (
        e.data &&
        e.data.source === 'surfsterre-wp-admin' &&
        e.data.type === 'CONFIG_UPDATE' &&
        e.data.config
      ) {
        console.log('[WP Config] 📡 Received live CONFIG_UPDATE via postMessage');
        const updated: WPGameConfig = e.data.config;
        saveCache(updated);
        setConfig(updated);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  return {
    config,
    outfits: config?.outfits ?? [],
    loading,
    error,
    refetch: fetchConfig,
  };
}
